import React, { useCallback, useEffect, useState } from 'react';
import type { OfficeGrade, OfficeLogDto, OfficeReportAgentDto, OfficeReportDto, OfficeReportTopicDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { askText } from '../dialogs';
import { Empty, ErrorBanner, Toggle, useNow } from '../ui';
import { AgentAvatar, timeOf } from './OfficePage';

/**
 * Pagella degli agenti: quante decisioni sono giuste secondo i voti di Marco, materia per materia.
 * I voti arrivano da soli (approva, scarta, correggi, annulla, «Visto», «Fatto») e da pochi controlli al giorno.
 * Una materia della Segreteria con la Pagella buona si può lasciar fare «da sola» anche da stagista.
 */

const GRADE_TONE: Record<OfficeGrade, string> = { ok: 'good', quasi: 'warn', male: 'bad', pochi: 'soft' };

export function OfficeReportPage({ ctx }: { ctx: Ctx }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<OfficeReportDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | string | null>(null);
  const now = useNow(60_000);

  const load = useCallback(
    async (more = false) => {
      try {
        const r = await ctx.api.get<OfficeReportDto>(`/api/office/report?days=${days}${more ? '&more=1' : ''}`);
        setData(r);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      }
    },
    [ctx.api, days],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const vote = async (e: OfficeLogDto, verdict: 'giusto' | 'sbagliato') => {
    let fix: string | null = '';
    if (verdict === 'sbagliato') {
      fix = await askText('Cosa doveva fare?', '', 'Facoltativo: se lo scrivi, diventa una regola per le prossime volte.');
      if (fix === null) return;
    }
    setBusy(e.id);
    try {
      if (verdict === 'sbagliato' && fix.trim().length >= 3) await ctx.api.post(`/api/office/log/${e.id}/correct`, { text: fix.trim() });
      else await ctx.api.post('/api/office/report/verdict', { logId: e.id, verdict });
      // La riga controllata sparisce subito; il resto si ricalcola
      setData((d) => (d ? { ...d, toCheck: d.toCheck.filter((x) => x.id !== e.id) } : d));
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const setAuto = async (t: OfficeReportTopicDto, on: boolean) => {
    setBusy(t.topic);
    try {
      setData(await ctx.api.put<OfficeReportDto>('/api/office/report/auto', { topic: t.topic, on, days }));
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="pg-page">
      <div className="of-head">
        <div>
          <h1>Pagella</h1>
          <div className="muted">Quante decisioni della squadra sono giuste, secondo i tuoi voti</div>
        </div>
        <div className="of-head-right">
          <div className="of-switch" role="tablist" aria-label="Periodo">
            {[30, 90].map((d) => (
              <button key={d} type="button" role="tab" aria-selected={days === d} className={days === d ? 'on' : ''} title={`I voti degli ultimi ${d} giorni`} onClick={() => setDays(d)}>
                {d} giorni
              </button>
            ))}
          </div>
        </div>
      </div>
      <ErrorBanner message={error} onClose={() => setError(null)} />

      {!data ? (
        <div className="panel muted">Caricamento…</div>
      ) : (
        <>
          <section className="panel pg-check">
            <header className="panel-head">
              <div className="grow">
                <h2>Da controllare</h2>
                <div className="subtitle">Un clic per dire se la decisione era giusta: così la Pagella diventa affidabile</div>
              </div>
            </header>
            {data.toCheck.length === 0 ? (
              <Empty icon="check">
                Per oggi hai controllato tutto.{' '}
                <button type="button" className="btn small ghost" title="Altre 5 decisioni da controllare, se hai tempo" onClick={() => void load(true)}>
                  Altre 5
                </button>
              </Empty>
            ) : (
              <div className="pg-items">
                {data.toCheck.map((e) => (
                  <div key={e.id} className="pg-item">
                    <AgentAvatar code={e.agent} size={32} />
                    <div className="pg-item-main">
                      <div className="pg-item-top">
                        <b>{e.title}</b>
                        <span className="muted small">{timeOf(e.at, now)}</span>
                      </div>
                      {e.mailSubject !== null && e.mailFrom && (
                        <div className="muted small pg-item-mail">
                          <Icon name="mail" size={13} /> «{e.mailSubject || 'senza oggetto'}» · {e.mailFrom}
                        </div>
                      )}
                      {e.reason && (
                        <div className="pg-item-why">
                          <span>Perché:</span> {e.reason}
                        </div>
                      )}
                    </div>
                    <div className="pg-vote">
                      <button type="button" className="btn small good" disabled={busy === e.id} title="La decisione è giusta" onClick={() => void vote(e, 'giusto')}>
                        <Icon name="check" size={14} /> Giusto
                      </button>
                      <button type="button" className="btn small" disabled={busy === e.id} title="La decisione è sbagliata: puoi scrivere cosa doveva fare" onClick={() => void vote(e, 'sbagliato')}>
                        <Icon name="x" size={14} /> Sbagliato
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {data.agents.map((a) => (
            <AgentReport key={a.code} a={a} minVotes={data.minVotes} busy={busy} onAuto={setAuto} />
          ))}
        </>
      )}
    </div>
  );
}

function AgentReport({ a, minVotes, busy, onAuto }: { a: OfficeReportAgentDto; minVotes: number; busy: number | string | null; onAuto: (t: OfficeReportTopicDto, on: boolean) => void }) {
  const autoNote =
    a.code !== 'segreteria'
      ? null
      : a.mode === 'attivo'
        ? 'È operativa: fa già tutto da sola, i voti servono a capire dove sbaglia.'
        : a.mode === 'spento'
          ? 'È ferma: non lavora finché non la riaccendi.'
          : 'Stagista: fa davvero solo le materie con «Da sola», le altre le scrive in «Cosa hanno fatto».';
  return (
    <section className="panel pg-agent">
      <header className="panel-head">
        <AgentAvatar code={a.code} size={38} />
        <div className="grow">
          <h2>{a.name}</h2>
          <div className="subtitle">{a.judged ? `${a.score}% giuste al primo colpo · ${a.judged} ${a.judged === 1 ? 'voto' : 'voti'}` : 'Ancora nessun voto'}</div>
        </div>
      </header>
      {autoNote && <div className="muted small pg-note">{autoNote}</div>}
      {a.topics.length === 0 ? (
        <div className="muted small pg-empty">Nessuna decisione nel periodo.</div>
      ) : (
        <div className="pg-topics">
          {a.topics.map((t) => (
            <div key={t.topic} className={`pg-topic g-${t.grade}`}>
              <div className="pg-topic-name">
                <b>{t.label}</b>
                <span className="muted small">
                  {t.decided} {t.decided === 1 ? 'decisione' : 'decisioni'} · {t.judged} {t.judged === 1 ? 'voto' : 'voti'}
                  {t.wrong ? ` · ${t.wrong} ${t.wrong === 1 ? 'sbagliata' : 'sbagliate'}` : ''}
                  {t.touched ? ` · ${t.touched} ritoccate` : ''}
                </span>
              </div>
              <div className="pg-bar" title={t.score === null ? 'Ancora nessun voto' : `${t.score}% giuste al primo colpo`}>
                <i style={{ width: `${t.score ?? 0}%` }} />
              </div>
              <span className="pg-score">{t.score === null ? '—' : `${t.score}%`}</span>
              <span className={`pill ${GRADE_TONE[t.grade]} pg-grade`} title={t.grade === 'pochi' ? `Il giudizio arriva dopo ${minVotes} voti` : undefined}>
                {t.gradeText}
              </span>
              {t.canAuto && a.mode === 'prova' ? (
                <label
                  className="pg-auto"
                  title={t.auto ? 'La fa da sola: al primo errore che segni torna a chiedere' : t.grade === 'ok' ? 'Lasciala fare da sola per questo tipo di mail' : 'Si può accendere quando la Pagella dice «Può fare da sola»'}
                >
                  <Toggle checked={t.auto} disabled={busy === t.topic || (!t.auto && t.grade !== 'ok')} onChange={(v) => onAuto(t, v)} />
                  <span className="small">Da sola</span>
                </label>
              ) : (
                <span className="pg-auto-off" />
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
